import { useState } from 'react';
import { X, Copy, Check, Code, ExternalLink } from 'lucide-react';

interface EmbedCodeGeneratorProps {
  flowDefinition: any;
  flowName: string;
  onClose: () => void;
}

export default function EmbedCodeGenerator({ flowDefinition, onClose }: EmbedCodeGeneratorProps) {
  const [copied, setCopied] = useState(false);
  const [embedType, setEmbedType] = useState<'iframe' | 'script'>('iframe');
  const [title, setTitle] = useState('Chat Assistant');
  const [primaryColor, setPrimaryColor] = useState('#9333ea');
  const [height, setHeight] = useState('600px');
  const [apiUrl, setApiUrl] = useState('');
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center'>('bottom-right');
  const [theme, setTheme] = useState<'light' | 'dark' | 'auto'>('light');
  const [animation, setAnimation] = useState<'slide' | 'fade' | 'bounce' | 'none'>('slide');
  const [customCSS, setCustomCSS] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Ottieni URL base corrente
  const getBaseUrl = () => {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    return 'https://your-domain.com';
  };

  const baseUrl = getBaseUrl();
  const flowDataEncoded = encodeURIComponent(JSON.stringify(flowDefinition));

  // Genera codice iframe
  const generateIframeCode = () => {
    const params = new URLSearchParams({
      flow: flowDataEncoded,
      title: title,
      color: primaryColor,
      height: height,
      theme: theme,
      animation: animation,
    });
    if (apiUrl) {
      params.append('apiUrl', apiUrl);
    }
    if (customCSS) {
      params.append('customCSS', encodeURIComponent(customCSS));
    }

    return `<iframe 
  src="${baseUrl}/embed?${params.toString()}"
  width="100%"
  height="${height}"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  allow="clipboard-read; clipboard-write"
></iframe>`;
  };

  // Genera codice script (per widget flottante)
  const generateScriptCode = () => {
    const params = new URLSearchParams({
      flow: flowDataEncoded,
      title: title,
      color: primaryColor,
      height: height,
      theme: theme,
      animation: animation,
    });
    if (apiUrl) {
      params.append('apiUrl', apiUrl);
    }
    if (customCSS) {
      params.append('customCSS', encodeURIComponent(customCSS));
    }

    // Calcola posizione CSS
    const positionStyles: Record<string, string> = {
      'bottom-right': 'bottom:20px;right:20px;',
      'bottom-left': 'bottom:20px;left:20px;',
      'top-right': 'top:20px;right:20px;',
      'top-left': 'top:20px;left:20px;',
      'center': 'top:50%;left:50%;transform:translate(-50%,-50%);',
    };

    // Animazione CSS
    const animationStyles: Record<string, string> = {
      'slide': 'animation:slideInUp 0.3s ease-out;',
      'fade': 'animation:fadeIn 0.3s ease-out;',
      'bounce': 'animation:bounceIn 0.5s ease-out;',
      'none': '',
    };

    const animationKeyframes = animation !== 'none' ? `
    @keyframes slideInUp {
      from { transform: translateY(100px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes bounceIn {
      0% { transform: scale(0.3); opacity: 0; }
      50% { transform: scale(1.05); }
      70% { transform: scale(0.9); }
      100% { transform: scale(1); opacity: 1; }
    }` : '';

    return `<script>
  (function() {
    var iframe = document.createElement('iframe');
    iframe.src = '${baseUrl}/embed?${params.toString()}';
    iframe.style.cssText = 'position:fixed;${positionStyles[position]}width:380px;height:${height};border:none;border-radius:8px;z-index:9999;box-shadow:0 4px 20px rgba(0,0,0,0.3);${animationStyles[animation]}';
    ${animation !== 'none' ? `var style = document.createElement('style');
    style.textContent = '${animationKeyframes}';
    document.head.appendChild(style);` : ''}
    document.body.appendChild(iframe);
  })();
</script>`;
  };

  const codeToCopy = embedType === 'iframe' ? generateIframeCode() : generateScriptCode();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Errore copia:', err);
      alert('Errore durante la copia. Seleziona manualmente il codice.');
    }
  };

  const previewUrl = `${baseUrl}/embed?${new URLSearchParams({
    flow: flowDataEncoded,
    title: title,
    color: primaryColor,
    height: height,
    ...(apiUrl && { apiUrl })
  }).toString()}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <Code className="w-6 h-6" />
            <h2 className="text-xl font-bold">Genera Codice Embed</h2>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Configurazione */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-gray-800">Configurazione Widget</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo di Embed
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="iframe"
                    checked={embedType === 'iframe'}
                    onChange={(e) => setEmbedType(e.target.value as 'iframe' | 'script')}
                  />
                  <span>Iframe (incorporato nella pagina)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="script"
                    checked={embedType === 'script'}
                    onChange={(e) => setEmbedType(e.target.value as 'iframe' | 'script')}
                  />
                  <span>Script (widget flottante)</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titolo Widget
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                placeholder="Chat Assistant"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Colore Primario
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-16 h-10 border-2 border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                  placeholder="#9333ea"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Altezza (solo iframe)
              </label>
              <input
                type="text"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                placeholder="600px"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API URL (opzionale - lascia vuoto per usare il default)
              </label>
              <input
                type="text"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                placeholder="http://localhost:8001"
              />
            </div>

            {/* Opzioni Avanzate */}
            <div className="pt-4 border-t">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700"
              >
                <span>{showAdvanced ? '▼' : '▶'}</span>
                Opzioni Avanzate
              </button>
              
              {showAdvanced && (
                <div className="mt-4 space-y-4">
                  {embedType === 'script' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Posizionamento (solo script)
                      </label>
                      <select
                        value={position}
                        onChange={(e) => setPosition(e.target.value as any)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                      >
                        <option value="bottom-right">In basso a destra</option>
                        <option value="bottom-left">In basso a sinistra</option>
                        <option value="top-right">In alto a destra</option>
                        <option value="top-left">In alto a sinistra</option>
                        <option value="center">Centro schermo</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tema
                    </label>
                    <select
                      value={theme}
                      onChange={(e) => setTheme(e.target.value as any)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                    >
                      <option value="light">🌞 Chiaro</option>
                      <option value="dark">🌙 Scuro</option>
                      <option value="auto">🔄 Automatico (segue sistema)</option>
                    </select>
                  </div>

                  {embedType === 'script' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Animazione di apertura
                      </label>
                      <select
                        value={animation}
                        onChange={(e) => setAnimation(e.target.value as any)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500"
                      >
                        <option value="slide">📈 Slide (da sotto)</option>
                        <option value="fade">✨ Fade (dissolvenza)</option>
                        <option value="bounce">🎯 Bounce (rimbalzo)</option>
                        <option value="none">🚫 Nessuna</option>
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CSS Personalizzato (opzionale)
                    </label>
                    <textarea
                      value={customCSS}
                      onChange={(e) => setCustomCSS(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 font-mono text-xs"
                      rows={4}
                      placeholder=".chat-widget { ... }"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      CSS aggiuntivo per personalizzare ulteriormente il widget
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Codice generato */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg text-gray-800">Codice da Inserire</h3>
              <div className="flex gap-2">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Anteprima
                </a>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copiato!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copia
                    </>
                  )}
                </button>
              </div>
            </div>
            <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{codeToCopy}</code>
            </pre>
          </div>

          {/* Istruzioni */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">📋 Istruzioni</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Il codice è pronto per essere inserito in qualsiasi sito web</li>
              <li>Per <strong>iframe</strong>: incolla il codice dove vuoi mostrare il widget nella pagina</li>
              <li>Per <strong>script</strong>: incolla il codice prima della chiusura del tag &lt;/body&gt;</li>
              <li>Assicurati che il backend API sia accessibile dal dominio dove inserisci il widget</li>
              <li>Per sicurezza, configura CORS sul backend per permettere richieste dal dominio esterno</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

